import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';


import * as API from '../apiConfig';

const initialClientForm = { clientName: '', phoneNumber: '', address: '' };
const initialOrderForm = {
  description: '',
  height: '',
  width: '',
  quantity: 1,
  rate: '',
  clientId: '',
  file: '',
};

function HomeComponent({ authData, onLogout }) {
  const navigate = useNavigate();
  const authToken = authData?.token;
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
  const [isBillsModalOpen, setIsBillsModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedClientIdForBill, setSelectedClientIdForBill] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [billClientSearch, setBillClientSearch] = useState('');
  const [clientForm, setClientForm] = useState(initialClientForm);
  const [orderForm, setOrderForm] = useState(initialOrderForm);
  const [orderClientSearch, setOrderClientSearch] = useState('');
  const [editingClientId, setEditingClientId] = useState(null);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isSubmittingClient, setIsSubmittingClient] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [isLoadingBills, setIsLoadingBills] = useState(false);
  const [isSubmittingBill, setIsSubmittingBill] = useState(false);
  const [isLoadingBillDetails, setIsLoadingBillDetails] = useState(false);
  const [clientMessage, setClientMessage] = useState('');
  const [clientError, setClientError] = useState('');
  const [orderMessage, setOrderMessage] = useState('');
  const [orderError, setOrderError] = useState('');
  const [billMessage, setBillMessage] = useState('');
  const [billError, setBillError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const fileInputRef = useRef(null);

  const [isScanning, setIsScanning] = useState(false);


  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editedOrder, setEditedOrder] = useState({});
  const [saving, setSaving] = useState(false);

  const [showSidebar, setShowSidebar] = useState(true);

  const submitOrderUpdate = async () => {
    if (!selectedOrder) return;

    setSaving(true);
    try {
      const payload = {};

      Object.keys(editedOrder).forEach(key => {
        if (editedOrder[key] !== selectedOrder[key]) {
          let value = editedOrder[key];

          // convert numeric fields to number
          if (
            ["height", "width", "quantity", "rate", "amount", "totalSquareFeet"]
              .includes(key)
          ) {
            value = parseFloat(value);
            if (isNaN(value)) value = 0;
          }

          if (key === "imagePath" || editedOrder[key] !== selectedOrder[key]) {
            payload[key] = value;
          }
        }
      });

      if (Object.keys(payload).length === 0) {
        setShowEditModal(false);
        return;
      }
      console.log(JSON.stringify(payload));
      const res = await fetch(`${API.ORDERS_API_URL}/${selectedOrder.id}`, {
        method: 'PUT',
        headers: getRequestHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        setOrders(prev =>
          prev.map(o =>
            o.id === data.order.id ? data.order : o
          )
        );
        setShowEditModal(false);
      } else {
        alert(data.error || "Update failed");
      }

    } catch (err) {
      alert("Error updating order");
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    const updatedOrder = {
      ...editedOrder,
      [field]: value
    };

    const height = parseFloat(updatedOrder.height) || 0;
    const width = parseFloat(updatedOrder.width) || 0;
    const quantity = parseFloat(updatedOrder.quantity) || 0;
    const rate = parseFloat(updatedOrder.rate) || 0;

    // Recalculate total sq ft if dimensions/quantity change
    if (["height", "width", "quantity"].includes(field)) {
      updatedOrder.totalSquareFeet = ((height * width) / 144) * quantity;
    }

    // Recalculate amount in ALL cases where it should depend on sq ft
    if (
      ["height", "width", "quantity", "rate", "totalSquareFeet"].includes(field)
    ) {
      const totalSqFt =
        parseFloat(updatedOrder.totalSquareFeet) ||
        ((height * width) / 144) * quantity;

      updatedOrder.amount = totalSqFt * rate;
    }

    setEditedOrder(updatedOrder);
  };


  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const pUrl = URL.createObjectURL(file);
    setPreviewUrl(pUrl);

    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(`${API.ORDERS_API_URL}/upload`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${authToken}`
      },
      body: formData
    });

    const url = await res.text();
    console.log(url);
    setUploadedUrl(url);
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // preview
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API.ORDERS_API_URL}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: formData
      });

      const url = await res.text();

      // IMPORTANT: update editedOrder so payload includes it
      setEditedOrder(prev => ({
        ...prev,
        imagePath: url
      }));

    } catch (err) {
      console.error("Image upload failed", err);
    }
  };

  useEffect(() => {
    fetch(`${API.TEST_API_URL}/api/hello`, {
      method: "GET",
      headers: getRequestHeaders()
    })
      .then(res => {
        if (res.status === 403) {
          navigate("/login");
        }
      })
      .catch(err => console.error(err));
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);
  const getRequestHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    return headers;
  }, [authToken]);

  const fetchClients = useCallback(async () => {
    setIsLoadingClients(true);
    setClientError('');

    try {
      const response = await fetch(API.CLIENTS_API_URL, {
        headers: getRequestHeaders()
      });

      // 🔴 Handle 403 globally here
      if (response.status === 403) {
        onLogout();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to load clients');
      }

      setClients(data.clients || []);
      setClientMessage(data.message || 'Clients retrieved successfully');

    } catch (fetchError) {
      setClientError(fetchError.message || 'Unable to load clients');
    } finally {
      setIsLoadingClients(false);
    }
  }, [getRequestHeaders, onLogout]);

  const fetchOrders = useCallback(async () => {
    setIsLoadingOrders(true);
    setOrderError('');
    try {
      const response = await fetch(API.ORDERS_API_URL, { headers: getRequestHeaders() });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to load orders');
      }
      setOrders(data.orders || []);
      setOrderMessage(data.message || 'Orders retrieved successfully');
    } catch (fetchError) {
      setOrderError(fetchError.message || 'Unable to load orders');
    } finally {
      setIsLoadingOrders(false);
    }
  }, [getRequestHeaders]);

  const fetchBills = useCallback(async (clientId) => {
    if (!clientId) {
      setBills([]);
      setSelectedBill(null);
      setBillMessage('');
      setBillError('');
      return;
    }

    setIsLoadingBills(true);
    setBillError('');
    try {
      const response = await fetch(`${API.BILLS_API_URL}/client/${clientId}`, {
        headers: getRequestHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to load bills');
      }
      setBills(data.bills || []);
      setBillMessage(
        data.message ||
        `${(data.bills || []).length} bill${(data.bills || []).length === 1 ? '' : 's'
        } loaded`
      );
    } catch (fetchError) {
      setBillError(fetchError.message || 'Unable to load bills');
    } finally {
      setIsLoadingBills(false);
    }
  }, [getRequestHeaders]);

  const fetchBillById = useCallback(
    async (billId) => {
      setIsLoadingBillDetails(true);
      setBillError('');
      try {
        const response = await fetch(`${API.BILLS_API_URL}/${billId}`, {
          headers: getRequestHeaders(),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Unable to load bill details');
        }
        setSelectedBill(data.bill || null);
      } catch (fetchError) {
        setBillError(fetchError.message || 'Unable to load bill details');
      } finally {
        setIsLoadingBillDetails(false);
      }
    },
    [getRequestHeaders]
  );

  useEffect(() => {
    if (isClientModalOpen) {
      fetchClients();
    }
  }, [fetchClients, isClientModalOpen]);

  useEffect(() => {
    if (isOrdersModalOpen) {
      fetchOrders();
      fetchClients();
    }
  }, [fetchClients, fetchOrders, isOrdersModalOpen]);

  useEffect(() => {
    if (isBillsModalOpen) {
      fetchOrders();
      fetchClients();
    }
  }, [fetchClients, fetchOrders, isBillsModalOpen]);

  useEffect(() => {
    if (isBillsModalOpen) {
      fetchBills(selectedClientIdForBill);
    }
  }, [fetchBills, isBillsModalOpen, selectedClientIdForBill]);

  const resetClientForm = () => {
    setClientForm(initialClientForm);
    setEditingClientId(null);
  };

  const resetOrderForm = () => {
    setOrderForm(initialOrderForm);
    setOrderClientSearch('');
    setPreviewUrl('');
    setUploadedUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // clears file input
    }
  }

  const resetBillSelection = () => {
    setBillClientSearch('');
    setSelectedClientIdForBill('');
    setSelectedOrderIds([]);
    setSelectedBill(null);
  };



  const handleClientFormChange = ({ target: { name, value } }) => {
    setClientForm((current) => ({ ...current, [name]: value }));
  };

  const handleOrderFormChange = ({ target: { name, value } }) => {
    setOrderForm((current) => ({ ...current, [name]: value }));
  };

  const handleOrderClientSearchChange = ({ target: { value } }) => {
    setOrderClientSearch(value);

    if (!value.trim()) {
      setOrderForm((current) => ({ ...current, clientId: '' }));
    }
  };

  const handleSelectOrderClient = (client) => {
    setOrderClientSearch(client.clientName || '');
    setOrderForm((current) => ({
      ...current,
      clientId: String(client.id),
    }));
  };

  const handleBillClientSearchChange = ({ target: { value } }) => {
    setBillClientSearch(value);

    if (!value.trim()) {
      setSelectedClientIdForBill('');
      setSelectedOrderIds([]);
      setSelectedBill(null);
    }
  };

  const handleSelectBillClient = (client) => {
    setBillClientSearch(client.clientName || '');
    setSelectedClientIdForBill(String(client.id));
    setSelectedOrderIds([]);
    setSelectedBill(null);
  };

  const handleToggleOrderSelection = (orderId) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId]
    );
  };



  const closeClientModal = () => {
    setIsClientModalOpen(false);
    setClientMessage('');
    setClientError('');
    resetClientForm();
  };



  const closeOrdersModal = () => {
    setIsOrdersModalOpen(false);
    setOrderMessage('');
    setOrderError('');
    resetOrderForm();
  };



  const closeBillsModal = () => {
    setIsBillsModalOpen(false);
    setBillMessage('');
    setBillError('');
    resetBillSelection();
  };

  const handleClientSubmit = async (event) => {
    event.preventDefault();
    setIsSubmittingClient(true);
    setClientError('');
    setClientMessage('');
    const isEditing = Boolean(editingClientId);
    const requestUrl = isEditing ? `${API.CLIENTS_API_URL}/${editingClientId}` : API.CLIENTS_API_URL;
    const requestMethod = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(requestUrl, {
        method: requestMethod,
        headers: getRequestHeaders(),
        body: JSON.stringify(clientForm),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to save client');
      }
      setClientMessage(
        data.message || (isEditing ? 'Client updated successfully' : 'Client added successfully')
      );
      resetClientForm();
      await fetchClients();
    } catch (submitError) {
      setClientError(submitError.message || 'Unable to save client');
    } finally {
      setIsSubmittingClient(false);
    }
  };

  const handleOrderSubmit = async (event) => {
    event.preventDefault();
    setIsSubmittingOrder(true);
    setOrderError('');
    setOrderMessage('');
    const payload = {
      ...orderForm,
      height: Number(orderForm.height),
      width: Number(orderForm.width),
      quantity: Number(orderForm.quantity),
      rate: Number(orderForm.rate),
      clientId: Number(orderForm.clientId),
      imagePath: uploadedUrl
    };

    try {
      const response = await fetch(API.ORDERS_API_URL, {
        method: 'POST',
        headers: getRequestHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to save order');
      }
      setOrderMessage(data.message || 'Order added successfully');
      resetOrderForm();
      await fetchOrders();
    } catch (submitError) {
      setOrderError(submitError.message || 'Unable to save order');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleBillSubmit = async () => {
    if (!selectedClientIdForBill || selectedOrderIds.length === 0) {
      setBillError('Select a client and at least one unbilled order');
      return;
    }

    setIsSubmittingBill(true);
    setBillError('');
    setBillMessage('');

    try {
      const response = await fetch(API.BILLS_API_URL, {
        method: 'POST',
        headers: getRequestHeaders(),
        body: JSON.stringify({
          clientId: Number(selectedClientIdForBill),
          orderIds: selectedOrderIds,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to generate bill');
      }
      setBillMessage(data.message || 'Bill generated successfully');
      setSelectedBill(data.bill || null);
      setSelectedOrderIds([]);
      await Promise.all([fetchBills(selectedClientIdForBill), fetchOrders()]);
    } catch (submitError) {
      setBillError(submitError.message || 'Unable to generate bill');
    } finally {
      setIsSubmittingBill(false);
    }
  };

  const handleEditClient = (client) => {
    setClientError('');
    setClientMessage(`Editing ${client.clientName}`);
    setEditingClientId(client.id);
    setClientForm({
      clientName: client.clientName || '',
      phoneNumber: client.phoneNumber || '',
      address: client.address || '',
    });
  };



  const handleEditOrder = (order) => {
    console.log(order)
    setSelectedOrder(order);

    // clone editable fields
    setEditedOrder({
      description: order.description || '',
      height: order.height || '',
      width: order.width || '',
      quantity: order.quantity || '',
      rate: order.rate || '',
      // amount optional (will be derived if backend expects rate)
      amount: order.amount || '',
      currentStage: order.currentStage || '',
      totalSquareFeet: order.totalSquareFeet || '',
      imagePath: order.imagePath || ''
    });

    setShowEditModal(true);
  };

  const handleStageChange = async (orderId, newStage) => {
    try {
      let stageToSend = newStage;
      let mode = null;

      if (
        newStage === "Delivered via Porter" ||
        newStage === "Client Picked Up"
      ) {
        stageToSend = "Delivered";
        mode = newStage;
      }

      let url = `${API.ORDERS_API_URL}/${orderId}/stage?stage=${encodeURIComponent(stageToSend)}`;

      if (mode) {
        url += `&mode=${encodeURIComponent(mode)}`;
      }

      await fetch(url, {
        method: "PUT",
        headers: getRequestHeaders(),
      });

      // update UI locally
      setOrders(prev =>
        prev.map(order =>
          order.id === orderId
            ? {
              ...order,
              currentStage: newStage,        // keep UI as selected option
              deleiverdThorugh: mode || order.deleiverdThorugh
            }
            : order
        )
      );

    } catch (error) {
      console.error("Failed to update stage", error);
    }
  };

  const handleGenerateBills = async () => {
    try {
      const res = await fetch(`${API.BILLS_API_URL}/generate-bill`, {
        method: "GET",
        headers: getRequestHeaders(),
      });

      if (!res.ok) {
        throw new Error("Failed to generate bills");
      }

      const data = await res.json();
      console.log(data)

      if (Array.isArray(data) && data.length > 0) {
        navigate("/generated-bills", { state: { bills: data } });
      } else {
        alert("No orders in delivered stage to generate bill");
      }

    } catch (error) {
      console.error(error);
      alert("Error generating bills");
    }
  };

  const handleDeleteClient = async (clientId) => {
    setClientError('');
    setClientMessage('');
    try {
      const response = await fetch(`${API.CLIENTS_API_URL}/${clientId}`, {
        method: 'DELETE',
        headers: getRequestHeaders(),
      });
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (!response.ok) {
        throw new Error(data.message || 'Unable to delete client');
      }
      setClientMessage(data.message || 'Client deleted successfully');
      if (editingClientId === clientId) {
        resetClientForm();
      }
      await fetchClients();
    } catch (deleteError) {
      setClientError(deleteError.message || 'Unable to delete client');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    // ask user to re-enter order id
    const input = prompt("Enter Order ID to confirm deletion:");

    // if cancelled
    if (input === null) return;

    // validate
    if (input.trim() !== String(orderId)) {
      alert("Wrong Order ID. Deletion cancelled.");
      return;
    }

    try {
      const res = await fetch(`${API.ORDERS_API_URL}/${orderId}`, {
        method: "DELETE",
        headers: getRequestHeaders(),
      });

      if (res.ok) {
        // remove from UI
        setOrders(prev => prev.filter(order => order.id !== orderId));
      } else {
        const data = await res.json();
        alert(data.error || "Failed to delete order");
      }

    } catch (err) {
      console.error(err);
      alert("Error deleting order");
    }
  };

  const handleDeleteBill = async (billId) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete bill #${billId}?`
    );

    if (!isConfirmed) {
      return;
    }

    setBillError('');
    setBillMessage('');

    try {
      const response = await fetch(`${API.BILLS_API_URL}/${billId}`, {
        method: 'DELETE',
        headers: getRequestHeaders(),
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.message || 'Unable to delete bill');
      }

      setBillMessage(data.message || 'Bill deleted successfully');

      if (selectedBill?.id === billId) {
        setSelectedBill(null);
      }

      await Promise.all([fetchBills(selectedClientIdForBill), fetchOrders()]);
    } catch (deleteError) {
      setBillError(deleteError.message || 'Unable to delete bill');
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/', { replace: true });
  };

  const handleExportBillPdf = () => {
    if (!selectedBill) {
      setBillError('Select a bill before exporting');
      return;
    }

    const selectedClient = clients.find(
      (client) => String(client.id) === String(selectedClientIdForBill)
    );
    const clientName = selectedClient?.clientName || 'Unknown Client';

    const orderRows = (selectedBill.orders || [])
      .map(
        (order) => `
          <tr>
            <td>${(clientName || "").slice(0, 3).toLowerCase()}-${order.orderId}</td>
            <td>${order.description || ''}</td>
            <td>${Number(order.height || 0).toFixed(2)}</td>
            <td>${Number(order.width || 0).toFixed(2)}</td>
            <td>${order.quantity ?? ''}</td>
            <td>${Number(order.rate || 0).toFixed(2)}</td>
            <td>${Number(order.totalSquareFeet || 0).toFixed(2)}</td>
            <td>${Number(order.amount || 0).toFixed(2)}</td>
          </tr>
        `
      )
      .join('');

    const printWindow = window.open('', '_blank', 'width=900,height=700');

    if (!printWindow) {
      setBillError('Popup blocked. Please allow popups to export the bill.');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill-${selectedBill.id}</title>
          <style>
            @page {
              size: A4;
              margin: 12mm;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              color: #212529;
              margin: 0;
              font-size: 10px;
              line-height: 1.2;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 10px;
            }
            .title {
              font-size: 18px;
              font-weight: 700;
              margin: 0 0 4px;
            }
            .meta {
              color: #6c757d;
              margin: 2px 0;
              font-size: 9px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 8px;
              table-layout: fixed;
              font-size: 9px;
            }
            th, td {
              border: 1px solid #dee2e6;
              padding: 4px 5px;
              text-align: left;
              word-wrap: break-word;
            }
            th {
              background: #f8f9fa;
              font-size: 8px;
              text-transform: uppercase;
            }
            .total {
              margin-top: 8px;
              text-align: right;
              font-size: 11px;
              font-weight: 700;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <p class="title">Unique Mirror</p>
              <p class="meta">Bill ID: #${selectedBill.id}</p>
              <p class="meta">Client: ${clientName}</p>
              <p class="meta">Date: ${selectedBill.formattedDate || selectedBill.date || ''}</p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>SR No.</th>
                <th>Description</th>
                <th>H</th>
                <th>W</th>
                <th>Quantity</th>
                <th>Rate</th>
                <th>Sq Ft</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${orderRows}
            </tbody>
          </table>

          <div class="total">Total Amount: ${Number(selectedBill.totalAmount || 0).toFixed(2)}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const totalSquareFeet =
    (Number(orderForm.height || 0) * Number(orderForm.width || 0)) / 144;
  const estimatedAmount =
    totalSquareFeet * Number(orderForm.quantity || 0) * Number(orderForm.rate || 0);

  const filteredOrdersForBilling = useMemo(() => {
    if (!selectedClientIdForBill) {
      return [];
    }

    return orders.filter(
      (order) =>
        String(order.clientId) === String(selectedClientIdForBill) && !order.billed
    );
  }, [orders, selectedClientIdForBill]);

  const selectedBillTotal = useMemo(
    () =>
      filteredOrdersForBilling
        .filter((order) => selectedOrderIds.includes(order.id))
        .reduce((sum, order) => sum + Number(order.amount || 0), 0),
    [filteredOrdersForBilling, selectedOrderIds]
  );

  const handleScan = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const filteredClientsForOrderSearch = useMemo(() => {
    const searchValue = orderClientSearch.trim().toLowerCase();

    if (!searchValue) {
      return clients.slice(0, 6);
    }

    return clients
      .filter((client) =>
        `${client.clientName || ''} ${client.phoneNumber || ''}`
          .toLowerCase()
          .includes(searchValue)
      )
      .slice(0, 6);
  }, [clients, orderClientSearch]);

  const filteredClientsForBillSearch = useMemo(() => {
    const searchValue = billClientSearch.trim().toLowerCase();

    if (!searchValue) {
      return clients.slice(0, 6);
    }

    return clients
      .filter((client) =>
        `${client.clientName || ''} ${client.phoneNumber || ''}`
          .toLowerCase()
          .includes(searchValue)
      )
      .slice(0, 6);
  }, [billClientSearch, clients]);

  return (
    <div className="billing-system">


      {/* Top Navbar */}
      <>
        {/* NAVBAR (light theme + toggler) */}
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">

            {/* Toggle */}
            <button
              className="btn btn-outline-secondary me-2"
              onClick={() => setShowSidebar(prev => !prev)}
            >
              ☰
            </button>

            <span className="navbar-brand fw-semibold">Billing System</span>

            <div className="d-flex gap-3 align-items-center">
              <span className="text-muted small">{authData?.email}</span>
              <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>
                Logout
              </button>
            </div>

          </div>
        </nav>

        {/* MAIN */}
        <div className="container-fluid">
          <div className="row">

            {/* SIDEBAR */}
            <div
              className={`${showSidebar ? "col-12 col-md-3 col-lg-2" : "d-none"
                } bg-light border-end p-3 min-vh-100`}
            >
              <ul className="nav flex-column gap-2">

                <li>
                  <NavLink to="/home" end className="btn btn-outline-secondary w-100 text-start">
                    Dashboard
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/home/scanner" className="btn btn-outline-secondary w-100 text-start">
                    QR Scanner
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/home/clients" className="btn btn-outline-secondary w-100 text-start">
                    Manage Clients
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/home/orders" className="btn btn-outline-secondary w-100 text-start">
                    Manage Orders
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/home/bills" className="btn btn-outline-secondary w-100 text-start">
                    Generate Bills
                  </NavLink>
                </li>

                <li>
                  <button
                    className="btn btn-outline-secondary w-100 text-start"
                    onClick={handleGenerateBills}
                  >
                    Generate Bills Auto
                  </button>
                </li>

              </ul>
            </div>

            {/* CONTENT */}
            <div
              className={`${showSidebar ? "col-12 col-md-9 col-lg-10" : "col-12"
                } p-4`}
            >
              <Outlet
                context={{
                  authData,
                  onLogout,
                  isScanning,
                  setIsScanning,
                  handleScan
                }}
              />
            </div>

          </div>
        </div>
      </>

      {isClientModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-fullscreen">
              <div className="modal-content border-0 shadow-sm rounded-4">

                {/* Header */}
                <div className="modal-header px-4 py-3">
                  <div>
                    <h5 className="modal-title mb-1">Client Management</h5>
                    <small className="text-muted">
                      Add, update, and manage your clients
                    </small>
                  </div>
                  <button className="btn-close" onClick={closeClientModal}></button>
                </div>

                {/* Body */}
                <div className="modal-body px-3 px-md-4 py-4">

                  <div className="row g-4">

                    {/* FORM */}
                    <div className="col-12 col-lg-5">
                      <div className="border rounded-3 p-3 p-md-4 h-100 bg-light">

                        <h6 className="fw-semibold mb-3">
                          {editingClientId ? 'Update Client' : 'Add Client'}
                        </h6>

                        {clientMessage && <div className="alert alert-success py-2">{clientMessage}</div>}
                        {clientError && <div className="alert alert-danger py-2">{clientError}</div>}

                        <form onSubmit={handleClientSubmit}>

                          <div className="mb-3">
                            <label className="form-label small">Client Name</label>
                            <input
                              type="text"
                              className="form-control"
                              name="clientName"
                              value={clientForm.clientName}
                              onChange={handleClientFormChange}
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label small">Phone</label>
                            <input
                              type="text"
                              className="form-control"
                              name="phoneNumber"
                              value={clientForm.phoneNumber}
                              onChange={handleClientFormChange}
                              required
                            />
                          </div>

                          <div className="mb-4">
                            <label className="form-label small">Address</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              name="address"
                              value={clientForm.address}
                              onChange={handleClientFormChange}
                              required
                            />
                          </div>

                          <div className="d-grid gap-2 d-md-flex">
                            <button
                              type="submit"
                              className="btn btn-primary w-100"
                              disabled={isSubmittingClient}
                            >
                              {isSubmittingClient
                                ? 'Saving...'
                                : editingClientId
                                  ? 'Update'
                                  : 'Add'}
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100"
                              onClick={resetClientForm}
                            >
                              Clear
                            </button>
                          </div>

                        </form>
                      </div>
                    </div>

                    {/* TABLE */}
                    <div className="col-12 col-lg-7">

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h6 className="fw-semibold mb-0">Clients</h6>
                          <small className="text-muted">
                            {isLoadingClients
                              ? 'Loading...'
                              : `${clients.length} total`}
                          </small>
                        </div>

                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={fetchClients}
                          disabled={isLoadingClients}
                        >
                          Refresh
                        </button>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle">

                          <thead className="table-light">
                            <tr>
                              <th>Name</th>
                              <th className="d-none d-md-table-cell">Phone</th>
                              <th className="d-none d-lg-table-cell">Address</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>

                          <tbody>

                            {!isLoadingClients && clients.length === 0 && (
                              <tr>
                                <td colSpan="4" className="text-center text-muted py-4">
                                  No clients found
                                </td>
                              </tr>
                            )}

                            {clients.map((client) => (
                              <tr key={client.id}>

                                <td>
                                  <div className="fw-semibold">{client.clientName}</div>
                                  <small className="text-muted d-md-none">
                                    {client.phoneNumber}
                                  </small>
                                </td>

                                <td className="d-none d-md-table-cell">
                                  {client.phoneNumber}
                                </td>

                                <td className="d-none d-lg-table-cell text-truncate" style={{ maxWidth: "200px" }}>
                                  {client.address}
                                </td>

                                <td className="text-end">
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => handleEditClient(client)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleDeleteClient(client.id)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>

                              </tr>
                            ))}

                          </tbody>
                        </table>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer px-4 py-3">
                  <button className="btn btn-secondary w-100 w-md-auto" onClick={closeClientModal}>
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}



      {isOrdersModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-fullscreen">
              <div className="modal-content border-0 shadow-sm rounded-4">

                {/* Header */}
                <div className="modal-header px-3 px-md-4 py-3">
                  <div>
                    <h5 className="modal-title mb-1">Order Management</h5>
                    <small className="text-muted">
                      Add new orders and manage all existing ones
                    </small>
                  </div>
                  <button className="btn-close" onClick={closeOrdersModal}></button>
                </div>

                {/* Body */}
                <div className="modal-body px-3 px-md-4 py-3 py-md-4">
                  <div className="row g-4">

                    {/* LEFT: FORM */}
                    <div className="col-12 col-lg-3">
                      <div className="border rounded-3 p-3 p-md-4 h-100 bg-light">

                        <h6 className="fw-semibold mb-3">Add Order</h6>

                        {orderMessage && <div className="alert alert-success py-2">{orderMessage}</div>}
                        {orderError && <div className="alert alert-danger py-2">{orderError}</div>}

                        <form onSubmit={handleOrderSubmit}>

                          {/* Client Search */}
                          <div className="mb-3">
                            <label className="form-label small">Client</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search client"
                              value={orderClientSearch}
                              onChange={handleOrderClientSearchChange}
                            />

                            <div className="mt-2">
                              {filteredClientsForOrderSearch.map((client) => (
                                <button
                                  key={client.id}
                                  type="button"
                                  className={`w-100 text-start btn btn-sm ${String(orderForm.clientId) === String(client.id)
                                    ? "btn-primary"
                                    : "btn-outline-secondary"
                                    } mb-1`}
                                  onClick={() => handleSelectOrderClient(client)}
                                >
                                  {client.clientName}
                                  <br />
                                  <small>{client.phoneNumber}</small>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* File */}
                          <div className="mb-3">
                            <label className="form-label small">Upload File</label>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="form-control"
                              onChange={handleFileChange}
                              required
                            />

                            {previewUrl && (
                              <img
                                src={previewUrl}
                                alt="preview"
                                className="img-fluid rounded border mt-2"
                                style={{ maxHeight: "150px" }}
                              />
                            )}
                          </div>

                          {/* Description */}
                          <div className="mb-3">
                            <label className="form-label small">Description</label>
                            <input
                              type="text"
                              className="form-control"
                              name="description"
                              value={orderForm.description}
                              onChange={handleOrderFormChange}
                              required
                            />
                          </div>

                          {/* Dimensions */}
                          <div className="row g-2">
                            <div className="col-6">
                              <input
                                type="number"
                                placeholder="Height"
                                className="form-control"
                                name="height"
                                value={orderForm.height}
                                onChange={handleOrderFormChange}
                                required
                              />
                            </div>
                            <div className="col-6">
                              <input
                                type="number"
                                placeholder="Width"
                                className="form-control"
                                name="width"
                                value={orderForm.width}
                                onChange={handleOrderFormChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Qty + Rate */}
                          <div className="row g-2 mt-2">
                            <div className="col-6">
                              <input
                                type="number"
                                placeholder="Qty"
                                className="form-control"
                                name="quantity"
                                value={orderForm.quantity}
                                onChange={handleOrderFormChange}
                                required
                              />
                            </div>
                            <div className="col-6">
                              <input
                                type="number"
                                placeholder="Rate"
                                className="form-control"
                                name="rate"
                                value={orderForm.rate}
                                onChange={handleOrderFormChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Summary */}
                          <div className="bg-white border rounded p-2 mt-3 small">
                            <div>Total sq ft: {totalSquareFeet.toFixed(2)}</div>
                            <div>Amount: {estimatedAmount.toFixed(2)}</div>
                          </div>

                          {/* Buttons */}
                          <div className="d-grid gap-2 d-md-flex mt-3">
                            <button
                              className="btn btn-primary w-100"
                              disabled={isSubmittingOrder}
                            >
                              {isSubmittingOrder ? "Saving..." : "Add Order"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100"
                              onClick={resetOrderForm}
                            >
                              Clear
                            </button>
                          </div>

                        </form>
                      </div>
                    </div>

                    {/* RIGHT: TABLE */}
                    <div className="col-12 col-lg-9">

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h6 className="fw-semibold mb-0">Orders</h6>
                          <small className="text-muted">
                            {isLoadingOrders ? "Loading..." : `${orders.length} total`}
                          </small>
                        </div>

                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={fetchOrders}
                          disabled={isLoadingOrders}
                        >
                          Refresh
                        </button>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle">

                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Img</th>
                              <th>QR</th>
                              <th>Description</th>
                              <th className="d-none d-md-table-cell">Client</th>
                              <th className="d-none d-lg-table-cell">Size</th>
                              <th>Qty</th>
                              <th className="d-none d-lg-table-cell">Rate</th>
                              <th>Square Foots</th>
                              <th className="d-none d-lg-table-cell">Amount</th>
                              <th>Status</th>
                              <th>Billed</th>
                              <th>Oprations</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!isLoadingOrders && orders.length === 0 && (
                              <tr>
                                <td colSpan="10" className="text-center text-muted py-4">
                                  No orders found
                                </td>
                              </tr>
                            )}

                            {orders.map((order) => (
                              <tr key={order.id} >
                                <td onClick={() => navigate(`/orders/${order.id}`)} style={{ cursor: "pointer" }}>
                                  {order.id}
                                </td>
                                <td>
                                  {order.imagePath ? (
                                    <img
                                      src={order.imagePath}
                                      alt=""
                                      style={{ width: 50, height: 50, objectFit: "cover" }}
                                      className="rounded"
                                    />
                                  ) : "—"}
                                </td>

                                <td>
                                  {order.qrCodePath ? (
                                    <img
                                      src={order.qrCodePath}
                                      alt=""
                                      style={{ width: 50, height: 50, objectFit: "cover" }}
                                      className="rounded"
                                    />
                                  ) : "—"}
                                </td>

                                <td>
                                  <strong>{order.description}</strong>
                                  <div className="small text-muted">
                                    {Number(order.totalSquareFeet || 0).toFixed(2)} sq ft
                                  </div>
                                </td>

                                <td className="d-none d-md-table-cell">
                                  {order.clientName}
                                </td>

                                <td className="d-none d-lg-table-cell">
                                  {order.height} x {order.width}
                                </td>

                                <td>{order.quantity}</td>

                                <td className="d-none d-lg-table-cell">
                                  {Number(order.rate || 0).toFixed(2)}
                                </td>
                                <td className="d-none d-lg-table-cell">
                                  {Number(order.totalSquareFeet || 0).toFixed(2)}
                                </td>

                                <td className="d-none d-lg-table-cell">
                                  {Number(order.amount || 0).toFixed(2)}
                                </td>

                                <td style={{ minWidth: "140px" }}>
                                  {order.billed ? (
                                    <span className="badge bg-secondary">Locked</span>
                                  ) : (
                                    <select
                                      className="form-select form-select-sm"
                                      value={
                                        order.currentStage === "Delivered"
                                          ? order.mode || "Delivered"
                                          : order.currentStage || ""
                                      }
                                      onChange={(e) =>
                                        handleStageChange(order.id, e.target.value)
                                      }
                                    >
                                      <option value="">Select Status</option>

                                      <option>Pending</option>
                                      <option>Processing</option>
                                      <option>Cutting</option>
                                      <option>Polishing</option>
                                      <option>Dispatched for Coloring</option>
                                      <option>Returned from Coloring</option>
                                      <option>Completed</option>

                                      {/* Delivered options */}
                                      <option>Delivered via Porter</option>
                                      <option>Client Picked Up</option>
                                    </select>
                                  )}
                                </td>

                                <td>
                                  {order.billed ? (
                                    <span className="badge bg-warning text-dark">
                                      Billed
                                    </span>
                                  ) : (
                                    <span className="badge bg-light text-muted">
                                      No
                                    </span>
                                  )}
                                </td>
                                <td className="text-end">
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => handleEditOrder(order)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleDeleteOrder(order.id)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>

                              </tr>
                            ))}
                          </tbody>

                        </table>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer px-3 px-md-4 py-3">
                  <button className="btn btn-secondary w-100 w-md-auto" onClick={closeOrdersModal}>
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showEditModal && (
        <div
          className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 2000 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-fullscreen-sm-down">
            <div className="modal-content rounded-4">

              {/* Header */}
              <div className="modal-header">
                <h5 className="modal-title">Edit Order</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>

              {/* Body */}
              <div className="modal-body d-flex flex-column gap-3">

                {/* Description */}
                <div>
                  <label className="form-label small text-muted">Description</label>
                  <input
                    className="form-control"
                    value={editedOrder.description}
                    onChange={e => handleFieldChange("description", e.target.value)}
                  />
                </div>

                {/* Dimensions */}
                <div>
                  <label className="form-label small text-muted">
                    Dimensions (Height × Width in inches)
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Height"
                        value={editedOrder.height}
                        onChange={e => handleFieldChange("height", e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Width"
                        value={editedOrder.width}
                        onChange={e => handleFieldChange("width", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="form-label small text-muted">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editedOrder.quantity}
                    onChange={e => handleFieldChange("quantity", e.target.value)}
                  />
                </div>

                {/* Rate */}
                <div>
                  <label className="form-label small text-muted">
                    Rate (₹ per sq ft)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={editedOrder.rate}
                    onChange={e => handleFieldChange("rate", e.target.value)}
                  />
                </div>

                {/* Total Sq Ft */}
                <div>
                  <label className="form-label small text-muted">
                    Total Sq Ft
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={editedOrder.totalSquareFeet}
                    onChange={e => handleFieldChange("totalSquareFeet", e.target.value)}
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="form-label small text-muted">Amount (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editedOrder.amount}
                    onChange={e => handleFieldChange("amount", e.target.value)}
                  />
                </div>

                {/* Image Preview */}
                <div>
                  <label className="form-label small text-muted">Image</label>

                  <input
                    type="file"
                    className="form-control"
                    onChange={handleEditImageChange}
                  />

                  <div className="mt-2 text-center">
                    <img
                      src={previewUrl || editedOrder.imagePath}
                      alt="preview"
                      style={{
                        width: "100%",
                        maxHeight: "200px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        background: "#f5f5f5"
                      }}
                    />
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-dark"
                  onClick={submitOrderUpdate}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {isBillsModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-fullscreen">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header">
                  <div>
                    <h2 className="modal-title h4 mb-1">Bill generation</h2>
                    <p className="text-secondary mb-0">
                      Choose one client, select unbilled orders, and generate a bill.
                    </p>
                  </div>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeBillsModal} />
                </div>

                <div className="modal-body p-4">
                  <div className="row g-4">
                    <div className="col-12 col-lg-5">
                      <div className="client-form-panel p-4">
                        <h3 className="h5 fw-bold mb-3">Create bill</h3>
                        {billMessage && <div className="alert alert-success">{billMessage}</div>}
                        {billError && <div className="alert alert-danger">{billError}</div>}
                        <div className="mt-3 mb-4">
                          <label className="form-label" htmlFor="bill-client-search">Filter by client</label>
                          <input
                            id="bill-client-search"
                            type="text"
                            className="form-control"
                            placeholder="Search client by name or phone"
                            value={billClientSearch}
                            onChange={handleBillClientSearchChange}
                            autoComplete="off"
                          />
                          <div className="client-search-results mt-2">
                            {filteredClientsForBillSearch.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                className={`client-search-item ${String(selectedClientIdForBill) === String(client.id) ? 'active' : ''
                                  }`}
                                onClick={() => handleSelectBillClient(client)}
                              >
                                <span>{client.clientName}</span>
                                <small>{client.phoneNumber}</small>
                              </button>
                            ))}
                            {billClientSearch.trim() && filteredClientsForBillSearch.length === 0 && (
                              <div className="text-secondary small px-2 py-1">
                                No matching clients found.
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="order-summary-box mb-3">
                          <span className="info-chip-label">Selected orders</span>
                          <strong>{selectedOrderIds.length}</strong>
                          <strong>Total amount: {selectedBillTotal.toFixed(2)}</strong>
                        </div>
                        <div className="bill-selection-list mb-4">
                          {!selectedClientIdForBill && (
                            <p className="text-secondary mb-0">Choose a client to see available unbilled orders.</p>
                          )}
                          {selectedClientIdForBill && filteredOrdersForBilling.length === 0 && (
                            <p className="text-secondary mb-0">No unbilled orders available for this client.</p>
                          )}
                          {filteredOrdersForBilling.map((order) => (
                            <label key={order.id} className="bill-order-item">
                              <input
                                type="checkbox"
                                className="form-check-input mt-1"
                                checked={selectedOrderIds.includes(order.id)}
                                onChange={() => handleToggleOrderSelection(order.id)}
                              />
                              <div>
                                <strong>{order.description}</strong>
                                <div className="small text-secondary">
                                  Qty {order.quantity} | Amount {Number(order.amount || 0).toFixed(2)}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <button type="button" className="btn btn-success" onClick={handleBillSubmit} disabled={isSubmittingBill}>
                            {isSubmittingBill ? 'Generating...' : 'Generate Bill'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={resetBillSelection} disabled={isSubmittingBill}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-7">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <div>
                          <h3 className="h5 fw-bold mb-1">Bill history</h3>
                          <p className="text-secondary mb-0">
                            {!selectedClientIdForBill
                              ? 'Select a client to load bills'
                              : isLoadingBills
                                ? 'Loading bills...'
                                : `${bills.length} bill${bills.length === 1 ? '' : 's'} found`}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-success"
                          onClick={() => fetchBills(selectedClientIdForBill)}
                          disabled={isLoadingBills || !selectedClientIdForBill}
                        >
                          Refresh
                        </button>
                      </div>
                      <div className="table-responsive client-table-wrap mb-3">
                        <table className="table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Bill ID</th>
                              <th>Date</th>
                              <th>Orders</th>
                              <th>Total</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!selectedClientIdForBill && (
                              <tr>
                                <td colSpan="5" className="text-center text-secondary py-4">Choose a client to view bill history.</td>
                              </tr>
                            )}
                            {selectedClientIdForBill && !isLoadingBills && bills.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center text-secondary py-4">No bills found yet.</td>
                              </tr>
                            )}
                            {bills.map((bill) => (
                              <tr key={bill.id}>
                                <td>#{bill.id}</td>
                                <td>{bill.formattedDate || bill.date}</td>
                                <td>{bill.orders?.length || 0}</td>
                                <td>{Number(bill.totalAmount || 0).toFixed(2)}</td>
                                <td className="text-end">
                                  <div className="d-inline-flex gap-2">
                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => fetchBillById(bill.id)} disabled={isLoadingBillDetails}>
                                      View
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteBill(bill.id)} disabled={isLoadingBills}>
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="bill-details-panel p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4 className="h6 fw-bold mb-0">Bill details</h4>
                          <div className="d-flex align-items-center gap-2">
                            {selectedBill && (
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={handleExportBillPdf}
                              >
                                Export PDF
                              </button>
                            )}
                            {isLoadingBillDetails && <span className="small text-secondary">Loading...</span>}
                          </div>
                        </div>
                        {!selectedBill && !isLoadingBillDetails && (
                          <p className="text-secondary mb-0">Select a bill from the list to view its orders.</p>
                        )}
                        {selectedBill && (
                          <>
                            <div className="d-flex flex-wrap gap-3 mb-3">
                              <div className="info-chip">
                                <span className="info-chip-label">Bill ID</span>
                                <strong>#{selectedBill.id}</strong>
                              </div>
                              <div className="info-chip">
                                <span className="info-chip-label">Date</span>
                                <strong>{selectedBill.formattedDate || selectedBill.date}</strong>
                              </div>
                              <div className="info-chip">
                                <span className="info-chip-label">Total</span>
                                <strong>{Number(selectedBill.totalAmount || 0).toFixed(2)}</strong>
                              </div>
                            </div>
                            <div className="table-responsive client-table-wrap">
                              <table className="table align-middle mb-0">
                                <thead>
                                  <tr>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Rate</th>
                                    <th>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedBill.orders?.map((order) => (
                                    <tr key={order.id}>
                                      <td>{order.description}</td>
                                      <td>{order.quantity}</td>
                                      <td>{Number(order.rate || 0).toFixed(2)}</td>
                                      <td>{Number(order.amount || 0).toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBillsModal}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}

export default HomeComponent;
